import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import { announcementsAPI, usersAPI } from '../services/api';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';

function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetType: 'individual',
    targetIds: [],
    expiresAt: '',
    priority: 'normal',
    attachments: []
  });

  const [uploadingFile, setUploadingFile] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [deptSearch, setDeptSearch] = useState('');

  // Fetch all data
  useEffect(() => {
    fetchAnnouncements();
    fetchDepartments();
    fetchUsers();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await announcementsAPI.getAllForAdmin();
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await usersAPI.getDepartmentsWithCount();
      console.log('Departments response:', response.data);
      setDepartments(response.data.departments || []);
      console.log('Departments loaded:', response.data.departments?.length || 0);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch all users without pagination
      const response = await usersAPI.getUsers({ limit: 10000, page: 1 });
      console.log('Users response:', response.data);
      setUsers(response.data.users || []);
      console.log('Users loaded:', response.data.users?.length || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleCreateNew = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      targetType: 'individual',
      targetIds: [],
      expiresAt: '',
      priority: 'normal',
      attachments: []
    });
    setUserSearch('');
    setDeptSearch('');
    setShowModal(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      targetType: announcement.targetType,
      targetIds: announcement.targetIds || [],
      expiresAt: announcement.expiresAt ? new Date(announcement.expiresAt).toISOString().slice(0, 16) : '',
      priority: announcement.priority,
      attachments: announcement.attachments || []
    });
    setUserSearch('');
    setDeptSearch('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await announcementsAPI.deleteAnnouncement(id);
      toast.success('Announcement deleted successfully');
      fetchAnnouncements();
      setConfirmDialog({ isOpen: false, id: null });
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const response = await announcementsAPI.uploadAttachment(file);
      const uploadedFile = response.data.file;
      
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, uploadedFile]
      }));
      
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    if (formData.targetType !== 'all_managers' && formData.targetIds.length === 0) {
      toast.error('Please select at least one target');
      return;
    }

    try {
      const payload = {
        ...formData,
        expiresAt: formData.expiresAt || null
      };

      if (editingAnnouncement) {
        await announcementsAPI.updateAnnouncement(editingAnnouncement.id, payload);
        toast.success('Announcement updated successfully');
      } else {
        await announcementsAPI.createAnnouncement(payload);
        toast.success('Announcement created successfully');
      }

      setShowModal(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    }
  };

  const handleTargetChange = (id) => {
    setFormData(prev => ({
      ...prev,
      targetIds: prev.targetIds.includes(id)
        ? prev.targetIds.filter(targetId => targetId !== id)
        : [...prev.targetIds, id]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return colors[priority] || colors.normal;
  };

  const getTargetTypeLabel = (targetType) => {
    const labels = {
      individual: 'Individual Users',
      department: 'Departments',
      all_managers: 'All Managers'
    };
    return labels[targetType] || targetType;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BellIcon className="h-8 w-8 mr-3 text-indigo-600" />
            Manage Announcements
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Create and manage announcements for your organization
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Announcement
        </button>
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <BellIcon className="mx-auto h-16 w-16 text-gray-400" />
          <p className="mt-4 text-gray-500">No announcements created yet</p>
          <button
            onClick={handleCreateNew}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create First Announcement
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* Mobile Cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {announcements.map((announcement) => (
              <div key={announcement.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-semibold text-gray-900">{announcement.title}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {getTargetTypeLabel(announcement.targetType)} • {formatDate(announcement.createdAt)}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(announcement.priority)}`}>
                    {announcement.priority}
                  </span>
                </div>
                {announcement.expiresAt && (
                  <div className="mt-1 text-xs text-gray-500">Expires: {formatDate(announcement.expiresAt)}</div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="text-gray-500">Recipients</div>
                  <div className="text-right text-gray-900">{announcement.totalRecipients || 0}</div>
                  <div className="text-gray-500">Read</div>
                  <div className="text-right text-gray-900">{announcement.readCount || 0}</div>
                  <div className="text-gray-500">Unread</div>
                  <div className="text-right text-gray-900">{announcement.unreadCount || 0}</div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => handleEdit(announcement)} className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100">
                    <PencilIcon className="h-4 w-4 mr-1" /> Edit
                  </button>
                  <button onClick={() => setConfirmDialog({ isOpen: true, id: announcement.id })} className="inline-flex items-center justify-center px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statistics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {announcements.map((announcement) => (
                <tr key={announcement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {announcement.title}
                    </div>
                    {announcement.expiresAt && (
                      <div className="text-xs text-gray-500">
                        Expires: {formatDate(announcement.expiresAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {getTargetTypeLabel(announcement.targetType)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {announcement.targetType === 'all_managers'
                        ? 'All'
                        : `${announcement.targetIds?.length || 0} selected`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(announcement.priority)}`}>
                      {announcement.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      Recipients: {announcement.totalRecipients || 0}
                    </div>
                    <div className="text-xs text-gray-500">
                      Read: {announcement.readCount || 0} | Unread: {announcement.unreadCount || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(announcement.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setConfirmDialog({ isOpen: true, id: announcement.id })}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">
                {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="You can use HTML tags for formatting"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Tip: You can use HTML tags like &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;br&gt;, &lt;p&gt;, etc.
                </p>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Target Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.targetType}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value, targetIds: [] })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="individual">Individual Users</option>
                  <option value="department">Departments</option>
                  <option value="all_managers">All Managers</option>
                </select>
              </div>

              {/* Target Selection */}
              {formData.targetType === 'individual' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Users <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({formData.targetIds.length} selected / {users.length} total)
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, targetIds: users.map(u => u.id) })}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, targetIds: [] })}
                        className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 text-sm"
                  />
                  <div className="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-gray-50">
                    {users.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No users found. Loading...</p>
                    ) : (
                      (() => {
                        const filteredUsers = users.filter(user => 
                          `${user.firstName} ${user.lastName} ${user.officialEmail}`.toLowerCase().includes(userSearch.toLowerCase())
                        );
                        return filteredUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No matching users</p>
                        ) : (
                          filteredUsers.map((user) => (
                            <label key={user.id} className="flex items-center py-2 hover:bg-white px-2 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.targetIds.includes(user.id)}
                                onChange={() => handleTargetChange(user.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm text-gray-900">
                                {user.firstName} {user.lastName} <span className="text-gray-500">({user.officialEmail})</span>
                              </span>
                            </label>
                          ))
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {formData.targetType === 'department' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Departments <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({formData.targetIds.length} selected / {departments.length} total)
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, targetIds: departments.map(d => d.id) })}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, targetIds: [] })}
                        className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search departments..."
                    value={deptSearch}
                    onChange={(e) => setDeptSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 text-sm"
                  />
                  <div className="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-gray-50">
                    {departments.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No departments found. Loading...</p>
                    ) : (
                      (() => {
                        const filteredDepts = departments.filter(dept => 
                          dept.name.toLowerCase().includes(deptSearch.toLowerCase())
                        );
                        return filteredDepts.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No matching departments</p>
                        ) : (
                          filteredDepts.map((dept) => (
                            <label key={dept.id} className="flex items-center py-2 hover:bg-white px-2 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.targetIds.includes(dept.id)}
                                onChange={() => handleTargetChange(dept.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm text-gray-900">
                                {dept.name} <span className="text-gray-500">({dept.userCount} users)</span>
                              </span>
                            </label>
                          ))
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Expiration Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments
                </label>
                <div className="space-y-2">
                  {formData.attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-700">{attachment.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-indigo-500">
                    <DocumentArrowUpIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {uploadingFile ? 'Uploading...' : 'Upload File'}
                    </span>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploadingFile}
                    />
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {editingAnnouncement ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null })}
        onConfirm={() => handleDelete(confirmDialog.id)}
        title="Delete Announcement"
        message="Are you sure you want to delete this announcement? This action cannot be undone."
      />
    </div>
  );
}

export default AdminAnnouncements;

